import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { fetchPaged } from "@/api/paged";
import { useAuth } from "@/auth/useAuth";

// notificacao-microservice. Porte de consulta-notificacao.controller.js (tela) e
// main.controller.js (sino do header) — mesmos endpoints, mesmo filtro.
export const NOTIFICACAO_PATH = "notificacao/WS/Notificacao";

export interface Notificacao {
  id: number;
  tituloPortal?: string;
  /** HTML vindo do backend — renderizar sempre via <HtmlSeguro>. */
  corpoPortal?: string;
  dataHoraInclusao?: string;
}

interface FiltroNotificacao {
  usuarioId: number;
  seApenasNaoLidas?: "S";
}

export type TipoLista = "nao-lidas" | "historico";

export async function getNotificacoes(usuarioId: number, tipo: TipoLista, size: number) {
  const filtro: FiltroNotificacao = tipo === "nao-lidas" ? { usuarioId, seApenasNaoLidas: "S" } : { usuarioId };
  return fetchPaged<Notificacao, FiltroNotificacao>(NOTIFICACAO_PATH, {
    page: 0,
    size,
    sort: { field: "id", direction: "DESC" },
    filtro,
  });
}

export async function dismissNotificacao(id: number): Promise<void> {
  await api.get(`${NOTIFICACAO_PATH}/Dismiss/${id}`);
}

export async function dismissTodas(usuarioId: number): Promise<void> {
  await api.post(`${NOTIFICACAO_PATH}/Usuario/${usuarioId}/DismissAll`);
}

// --- Hooks -----------------------------------------------------------------

const KEY = ["notificacao"] as const;

interface OpcoesLista {
  /** Sino do header: paridade com o polling de 60s do MainCtrl, sem toast de erro. */
  poll?: boolean;
}

export function useNotificacoes(tipo: TipoLista, size: number, { poll }: OpcoesLista = {}) {
  const usuarioId = useAuth().usuario?.usuarioId;
  return useQuery({
    queryKey: [...KEY, usuarioId, tipo, size],
    queryFn: () => getNotificacoes(usuarioId!, tipo, size),
    enabled: usuarioId != null,
    // "Mostrar mais" só aumenta o size: mantém a lista atual na tela enquanto busca.
    placeholderData: keepPreviousData,
    ...(poll ? { refetchInterval: 60_000, meta: { silent: true } } : {}),
  });
}

export function useDismissNotificacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: dismissNotificacao,
    // Sino e tela leem a mesma fonte; lida some de "não lidas" nos dois.
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDismissTodas() {
  const qc = useQueryClient();
  const usuarioId = useAuth().usuario?.usuarioId;
  return useMutation({
    mutationFn: () => dismissTodas(usuarioId!),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
